import { AxiosError } from 'axios';
import { toast } from 'react-toastify';
import { ClientGeolocation } from './types/common';
import { IObject } from './interface/common';
import { sortBy } from 'lodash';

export function notify(type: string, message: string, customOptions: any = {}) {
  let option = { toastId: 'bor-toast', ...customOptions };
  if (type === 'error') {
    return toast.error(message, option);
  }

  if (type === 'success') {
    return toast.success(message, option);
  }

  if (type === 'warning') {
    return toast.warning(message, option);
  }

  return toast(message, option);
}

export function catchError(e: unknown, handleError: (errorMessage: string) => void) {
  if (typeof e === 'string') {
    handleError(e);
  } else if (e instanceof AxiosError) {
    handleError(e?.response?.data?.message);
  } else if (e instanceof Error) {
    handleError(e.message);
  }
}

export const downloadCSV = async (data: any, nameFileDownload?: string) => {
  const blobURL = window.URL.createObjectURL(new Blob([data]));

  // Create new tag for download file
  const anchor = document.createElement('a');
  if (nameFileDownload) {
    anchor.download = nameFileDownload;
  } else {
    anchor.download = 'file.csv';
  }

  anchor.href = blobURL;
  anchor.dataset.downloadurl = ['text/csv', anchor.download, anchor.href].join(':');
  anchor.click();
  // Remove URL.createObjectURL. The browser should not save the reference to the file.
  setTimeout(() => {
    // For Firefox it is necessary to delay revoking the ObjectURL
    URL.revokeObjectURL(blobURL);
  }, 100);
};
export const downloadPDF = async (data: any, nameFileDownload?: string) => {
  const blob = new Blob([data], { type: 'application/pdf' });
  const blobURL = window.URL.createObjectURL(blob);

  const anchor = document.createElement('a');
  anchor.href = blobURL;
  anchor.download = nameFileDownload || 'file.pdf';
  anchor.click();

  setTimeout(() => {
    URL.revokeObjectURL(blobURL);
  }, 100);
};

export function handleApiError(
  e: unknown,
  setFieldError?: (field: string, message: string | undefined) => void,
  isNotify: boolean = true
) {
  if (isNotify) {
    catchError(e, (message) => {
      notify('error', message || 'Something went wrong.');
    });
  }

  if (setFieldError && e instanceof AxiosError && e?.response?.data?.data) {
    for (let data of e.response.data.data) {
      setFieldError(data.field, data.message);
    }
  }
}

export function convertTextTimeToNumber(value: string) {
  const timeArray: Array<string> = value.toLowerCase().split('h');
  let hour: number = 0;
  let minute: number = 0;

  const time1st: number = +timeArray[0].replace(/[^0-9]+/g, '');

  // Case only has minutes
  if (timeArray[0].indexOf('m') > -1) {
    return time1st;
  }

  hour = time1st * 60;
  // Case has minutes
  if (timeArray[1]) {
    minute = +timeArray[1].replace(/[^0-9]+/g, '');
  }

  return hour + minute;
}

export function formatDate(dateString: string): string {
  const date = new Date(dateString);
  return new Intl.DateTimeFormat('en-US', { year: 'numeric', month: 'long', day: 'numeric' }).format(date);
}

export function convertNumberToTime(number: number) {
  const hours = Math.floor(number / 3600);
  const minutes = Math.floor((number - hours * 3600) / 60);
  const seconds = number - hours * 3600 - minutes * 60;

  let hoursText = hours.toString();
  let minutesText = minutes.toString();
  let secondsText = seconds.toString();

  if (hours < 10) hoursText = '0' + hoursText;
  if (minutes < 10) minutesText = '0' + minutesText;
  if (seconds < 10) secondsText = '0' + secondsText;

  return hoursText + ':' + minutesText + ':' + secondsText;
}

export function getTimezone() {
  let timezone = 'UTC';
  try {
    timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch (e) {
    console.warn('Cannot get timezone');
  }

  return timezone;
}

export function formatCurrency(value: any) {
  const numericValue = parseFloat(value);
  if (isNaN(numericValue)) {
    return '';
  }

  // Determine if the value is negative and format accordingly
  const isNegative = numericValue < 0;
  const absoluteValue = Math.abs(numericValue);

  return (
    (isNegative ? '-' : '') +
    '$' +
    absoluteValue.toLocaleString('en-US', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 6,
    })
  );
}

export function formatPercent(value: any) {
  const numericValue = parseFloat(value);
  if (isNaN(numericValue)) {
    return '';
  }

  return (
    numericValue.toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }) + '%'
  );
}

/**
 * React doesn't work fine with null, undefined and string
 *
 * cast value from boolean true/false to string "true"/"false"
 * cast null and undefined to ""
 *
 * @param value
 * @returns
 */
export function cast(value: any) {
  if (typeof value === 'boolean') return String(value);

  return value ?? '';
}

/**
 * Request browser geolocation
 * @returns ClientGeolocation
 */
export function getClientGeolocation(): Promise<ClientGeolocation> {
  return new Promise((resolve, reject) => {
    // The browser doesn't support geolocation
    if (!navigator.geolocation) return reject({ code: 0 });

    navigator.geolocation.getCurrentPosition(
      async (position: GeolocationPosition) => {
        const lat: number = position.coords.latitude;
        const long: number = position.coords.longitude;

        return resolve({ lat, long });
      },
      (error: GeolocationPositionError) => {
        return reject(error);
      }
    );
  });
}

export function appendToFormData(
  formData: FormData,
  data: any,
  parentKey: string = '',
  checkFile: Array<string> = []
): void {
  if (data && typeof data === 'object') {
    if (Array.isArray(data)) {
      // Handle arrays
      data.forEach((item, index) => {
        appendToFormData(formData, item, `${parentKey}[${index}]`, checkFile);
      });
    } else {
      // Handle objects
      for (const key in data) {
        if (data.hasOwnProperty(key)) {
          const value = data[key];
          const newKey = parentKey ? `${parentKey}[${key}]` : key;

          if (checkFile.length && checkFile.includes(key) && value) {
            if (Array.isArray(value)) {
              if (value.length > 0) {
                value.forEach((item) => {
                  formData.append(newKey, item.file);
                });
              }
            } else if (value) {
              formData.append(newKey, value);
            }
          } else if (!(checkFile.includes(key) && !value)) {
            appendToFormData(formData, value, newKey, checkFile);
          }
        }
      }
    }
  } else {
    if (parentKey) {
      formData.append(parentKey, data !== undefined ? data : '');
    }
  }
}

export const stringToColor = (string: string) => {
  let hash = 0;
  let i;

  for (i = 0; i < string.length; i += 1) {
    hash = string.charCodeAt(i) + ((hash << 5) - hash);
  }

  let color = '#';

  for (i = 0; i < 3; i += 1) {
    const value = (hash >> (i * 8)) & 0xff;
    color += `00${value.toString(16)}`.slice(-2);
  }

  return color;
};

export const findRecursiveItem = (id: string, items: IObject) => {
  for (let i in items) {
    if (i === id) return items[i];

    if (typeof items[i] === 'object') {
      const foundItem: any = findRecursiveItem(id, items[i]);
      if (foundItem) return foundItem;
    }
  }
};

export const flatRecursive = (
  items: [IObject | Array<any>],
  flatList: Array<any> = [],
  pushObjectKey: boolean = false
) => {
  const list = flatList;
  if (typeof items !== 'object') {
    list.push(items);
    return list;
  }

  for (let i in items) {
    const item: any = items[i as keyof [IObject | Array<any>]];
    if (typeof item === 'object' && item) {
      if (pushObjectKey) {
        list.push(i);
      }
      flatRecursive(item, list, pushObjectKey);
    } else {
      list.push(item);
    }
  }

  return list;
};

export const blobToBase64 = async (blob: Blob): Promise<string | ArrayBuffer | null> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      resolve(reader.result);
    };
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
};

export const sortByKey = (list: any, key: string) => {
  return sortBy(list, [
    function (o) {
      return (o[key] ?? '$$Unknown').toLowerCase();
    },
  ]);
};

export const generatePersonName = (firstName?: string | null, lastName?: string | null) => {
  const personNameArray: Array<string> = [];
  if (firstName) personNameArray.push(firstName);
  if (lastName) personNameArray.push(lastName);

  return personNameArray.join(' ');
};

export const labelToKey = (label: string): string => {
  return label
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
};
